import { Component } from 'react';
import classnames from 'classnames';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import { WithStyles, withStyles } from '@mui/styles';
import { FormHelperText } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LDFlagSet } from 'launchdarkly-react-client-sdk';

// Contexts
import { withTenant, withTranslate } from '../../../core/TenantProvider';
import withFlags from '../../../core/FlagsProvider';
// Components - Atoms, Organisms,Containers
import APSelectItem from './APSelectItem';
// Files in the same folder
import ErrorIcon from '../APInput/assets/icon-error.svg';
import ValidIcon from '../APInput/assets/icon-tick-outline.svg';
import APSelectStyles from './styles/APSelect.style';
// Types
import { DropdownOption } from '../../../core/types/Currencies';
// Reducer
import { EtvOptions } from '../../../redux/modules/refer/reducer';
// Utils
import GetDropdownReferenceData from '../../../core/utils/GetDropdownReferenceData';
import { GetCurrencyOptionList } from '../../../core/utils/GetCurrenciesData';
// Constants
import {
  BUYING_CURRENCY_LABEL,
  MASK_CLASS,
  REFERENCE_DATA_ACCOUNT_SELECT_PROFILE_MANAGEMENT_TYPE,
  REFERENCE_DATA_ACCOUNT_SELECT_TYPE,
  REFERENCE_DATA_EMPLOYMENT_STATUS_TYPE,
  REFERENCE_DATA_TRANSACTION_TYPE,
  SELLING_CURRENCY_LABEL
} from '../../../core/utils/Constants/Constants';

type ReferenceData = {
  label: string;
  value: any;
  displayName: string;
  icon?: string,
  isIndustryDependent?: boolean
};

interface APSelectProps extends WithStyles<typeof APSelectStyles> {
  children?: any;
  className: string;
  data?: any;
  label?: string;
  onBlur?: any;
  onChange?: any;
  onFocus?: any;
  name?: string;
  value?: any;
  type?: string;
  displayName?: string;
  helperText?: string;
  error?: any;
  hasErrorState?: any;
  inputProps?: any;
  dropdownOption?: EtvOptions;
  currencies: DropdownOption;
  disableClearValue?: boolean;
  translate: Function;
  flags?: LDFlagSet;
  isCoralogixMasked?: boolean;
  disabled?: boolean;
}

const REFERENCE_DATA = GetDropdownReferenceData();

class APSelectRef extends Component<APSelectProps, {}> {

  state = {
    icon: undefined,
    inputValue: undefined,
    touched: false
  };

  static getDerivedStateFromProps(props: APSelectProps, state: any) {
    if (state.inputValue !== undefined && state.inputValue !== '') {
      return state;
    }

    return {
      ...state,
      inputValue: props.value
    };
  }

  constructor(props: APSelectProps) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.getReferenceDataValue = this.getReferenceDataValue.bind(this);
    this.importIcon = this.importIcon.bind(this);
    this.handleIconImport = this.handleIconImport.bind(this);
    this.filterOptions = this.filterOptions.bind(this);
  }

  componentDidMount() {
    this.refresh();
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.value !== prevProps.value) {
      this.refresh();
    }
  }

  getReferenceDataValue() {
    const {
      data,
      name,
      type,
      dropdownOption,
      currencies,
      flags = {}
    } = this.props;

    let reference_data: any = data;
    if (!reference_data && (type === REFERENCE_DATA_ACCOUNT_SELECT_TYPE || type === REFERENCE_DATA_ACCOUNT_SELECT_PROFILE_MANAGEMENT_TYPE) && dropdownOption) {
      reference_data = dropdownOption.options;
    }
    if (!reference_data && type) {
      reference_data = REFERENCE_DATA[type].options;
    }

    const shouldEnableComplianceTestingOptionsRAC: boolean = flags?.enableComplianceTestingOptionRac ?? true;

    if (reference_data && type === REFERENCE_DATA_EMPLOYMENT_STATUS_TYPE && !shouldEnableComplianceTestingOptionsRAC) {
      reference_data = reference_data.slice(0, 9)
    }

    if (reference_data && type === REFERENCE_DATA_TRANSACTION_TYPE && dropdownOption) {
      reference_data = dropdownOption.options;
    }

    const isBuySellCurrenciesData: boolean = name === SELLING_CURRENCY_LABEL || name === BUYING_CURRENCY_LABEL;
    if (reference_data && name && isBuySellCurrenciesData) {
      reference_data = currencies[name].options;
    } else if (reference_data && type === 'currency' && !isBuySellCurrenciesData) {
      reference_data = GetCurrencyOptionList().options;
    }

    return reference_data;
  }

  importIcon(iconName: string) {
    import(`../../../core/assets/CountriesList/${iconName}.svg`)
      .then(({ default: icon }) => {
        this.setState({
          icon
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }

  handleIconImport(selection: any) {
    if (selection && selection.icon) {
      this.importIcon(selection.icon);
    } else {
      this.setState({
        icon: undefined,
      });
    }
  }

  refresh() {
    const {
      value: inputValue,
      type,
      displayName,
    } = this.props;

    let value: any = null;
    const reference_data: any = this.getReferenceDataValue();
    const findOption = (option: { value: any; displayName: string; }) => {
      return option.value === inputValue && (!displayName || option.displayName === displayName);
    }

    if (reference_data && type === 'dialing') {
      value = reference_data.find(findOption);
    } else {
      if (inputValue) {
        value = reference_data.find(findOption);
      } else {
        this.setState({
          inputValue: ''
        });
      }
    }

    this.handleIconImport(value);

  }

  onChange(event: any, selection: any) {
    const {
      name,
      onChange,
      type
    } = this.props;
    this.setState({
      inputValue: undefined,
      touched: true
    }, () => {
      if (onChange) {
        const target: { name: string | undefined; value: any; displayName?: string; isIndustryDependent?: boolean; } = {
          name,
          value: selection !== null ? selection.value : '',
        };

        if (type === 'dialing') {
          target.displayName = selection !== null ? selection.displayName : '';
        }

        if (type === 'employmentStatus') {
          target.isIndustryDependent = selection !== null ? selection.isIndustryDependent : '';
        }

        onChange({ target });
      }
    });

    if (type === 'dialing') {
      this.handleIconImport(selection);
    }
  }

  onInputChange(evt: any, value: string, reason: string) {
    if (value.length > 0 && reason === 'input') {
      this.setState({
        inputValue: value,
        touched: true,
      });
    }
  }

  filterOptions(options: ReferenceData[], { inputValue }) {
    let filteredOptions: ReferenceData[] | null = null;
    const isDialing: boolean = this.props.type === "dialing";
    const getInputValue: string = inputValue.toLowerCase();

    if (isDialing) {
      filteredOptions = options.filter((option: ReferenceData) => option.value.includes(inputValue));
    } else {
      filteredOptions = options.filter((option: ReferenceData) => option.label.toLowerCase().includes(getInputValue));
    }

    return filteredOptions;
  }

  render() {
    const {
      className,
      classes,
      label,
      onBlur,
      onFocus,
      name,
      value: inputValue,
      helperText,
      error,
      hasErrorState,
      type,
      disableClearValue = false,
      isCoralogixMasked = false,
      disabled = false
    } = this.props;

    const isAccountSelectType: boolean = type === 'accountSelect';
    const formControlCls: string = classnames(className, classes.formControl);
    const customIconClassName: string = classnames(classes.inputIcon, {
      'customizeInputIcon': type === 'portalLanguage',
    });
    const customPopperClass: string = classnames(classes.popper, {
      [classes.customPopper]: type === 'portalLanguage',
      [classes.customAccountPopper]: isAccountSelectType,
      [MASK_CLASS]: isCoralogixMasked

    });

    const reference_data: any = this.getReferenceDataValue();

    let value;
    if (reference_data) {
      reference_data.forEach((option: ReferenceData) => {
        if (option.value === inputValue) {
          value = option;
        }
      });
    }

    return (
      <FormControl className={formControlCls} variant="outlined" disabled={disabled}>
        <Autocomplete
          id={`AutoCompleteSearch-${label}`}
          data-testid='autocomplete-search'
          classes={{ popper: customPopperClass, popupIndicator: classes.popupIndicator }}
          className={`${isCoralogixMasked ? MASK_CLASS : ''}`}
          disabled={disabled}
          disableClearable={!this.state.inputValue || disableClearValue}
          onBlur={onBlur}
          onChange={this.onChange}
          onFocus={onFocus}
          onInputChange={this.onInputChange}
          options={reference_data}
          noOptionsText={"No results found"}
          getOptionLabel={(option: ReferenceData) => (type === 'dialing' ? option.value : option.label)}
          popupIcon={<ExpandMoreIcon />}
          filterOptions={this.filterOptions}
          renderInput={(params) => (
            <div className={classes.textFieldWrapper}>
              {this.state.icon && (
                <img className={customIconClassName} src={this.state.icon} alt={value?.icon ? value.icon.split('/')[1] + '.svg' : ''} />
              )}
              <TextField
                {...params}
                className={this.state.icon && classes.hasIcon}
                name={name}
                label={label}
                variant="outlined"
                error={error}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  shrink: !!(this.state.inputValue || value || this.state.icon || params.inputProps.value)
                }}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: `new-${name}`,
                  form: {
                    autocomplete: 'off',
                  },
                  "data-testid": "select-input"
                }}
              />
            </div>
          )}
          renderOption={
            (props, option) =>
              <APSelectItem
                type={isAccountSelectType && 'accountSelect'}
                {...option}
                {...props}
                key={`${option.label}-${option.value}`}
              />}
          value={value}
        />
        {hasErrorState && error && (
          <img className={classes.stateIcon} src={ErrorIcon} alt="Invalid input icon" />
        )}
        {hasErrorState && this.state.touched && !error && (
          <img className={classes.stateIcon} src={ValidIcon} alt="Valid input icon" />
        )}
        {helperText && (
          <FormHelperText className={classes.helperText} error={error}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    )
  }
}

export default withFlags(
  withTenant(withTranslate(withStyles(APSelectStyles)(APSelectRef)))
);